// TaskDetailModal: Hub d'actions rapides pour les missions dans la vue Equipe
// Combine consultation (opérateur, date, service) et actions directes (statut, champs de saisie)
// Statut modifiable directement via badge interactif — aucune modale secondaire nécessaire
// Champs de saisie éditables directement dans la section Détails
// "Modifier" réservé aux modifications structurelles (réattribution, horaire, date)
// UPDATED: Séparation visuelle zone consultation / zone action
// UPDATED: Validation champs obligatoires avant passage au statut "Terminée"
// UPDATED: Auto-sync via selectedTaskForReassign quand les tasks se rechargent
// FIXED: Converted to createPortal via getPortalRoot() to prevent React reconciliation conflicts
// UPDATED: Added "Informations" section header for visual consistency across all body sections

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getPortalRoot } from '../../lib/portalUtils';
import { X, Pencil, Trash2, Info } from 'lucide-react';
import type { AssignedTaskWithDetails } from '../../lib/execution';
import { getFullName } from '../../lib/supabase';
import { getRecurrence, getRecurrencePattern } from '../../lib/recurrence';
import { getServiceColorClass } from '../../lib/services';
import type { RecurrenceRule } from '../../lib/supabase';
import { TaskDetailStatusBadge } from './TaskDetailStatusBadge';
import { TaskDetailItems } from './TaskDetailItems';
import { TaskActivityHistory } from './TaskActivityHistory';

interface Operator {
  id: string;
  first_name: string;
  last_name: string;
  username: string;
}

interface TaskDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: AssignedTaskWithDetails | null;
  operator: Operator | null;
  serviceName?: string;
  serviceColor?: string;
  onEdit: () => void;
  onDelete: () => void;
  isAdmin?: boolean;
  onStatusChange: (newStatus: 'not_started' | 'completed' | 'overdue') => Promise<void>;
  onTextInputBatchSave: (values: Record<string, string>) => Promise<void>;
}

export function TaskDetailModal({
  isOpen,
  onClose,
  task,
  operator,
  serviceName,
  serviceColor,
  onEdit,
  onDelete,
  isAdmin = false,
  onStatusChange,
  onTextInputBatchSave,
}: TaskDetailModalProps) {
  const [recurrenceRule, setRecurrenceRule] = useState<RecurrenceRule | null>(null);

  useEffect(() => {
    if (!task || !task.is_recurring || !task.template_id) {
      setRecurrenceRule(null);
      return;
    }

    let isMounted = true;
    const loadRecurrence = async () => {
      try {
        const rule = await getRecurrence(task.template_id);
        if (isMounted) setRecurrenceRule(rule);
      } catch {}
    };

    loadRecurrence();
    return () => { isMounted = false; };
  }, [task?.id, task?.is_recurring, task?.template_id]);

  if (!isOpen || !task) return null;

  const formatDateTimeRange = (dateStr: string, timeStr: string | null, durationMinutes: number) => {
    const date = new Date(dateStr);
    const dateFormatted = date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

    if (!timeStr) return `${dateFormatted} (heure non définie)`;

    const [hours, minutes] = timeStr.split(':').map(Number);
    const startDate = new Date(date);
    startDate.setHours(hours, minutes);

    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + durationMinutes);

    const startTime = startDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    const endTime = endDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    return `${dateFormatted} de ${startTime} à ${endTime}`;
  };

  const formatCompletedAt = (dateTimeStr: string) => {
    const date = new Date(dateTimeStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const mins = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} à ${hours}:${mins}`;
  };

  const canShowEditDelete = task.status !== 'completed' || isAdmin;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">

        {/* ── Header ─────────────────────────────────────────── */}
        <div className="px-5 pt-5 pb-4 border-b border-gray-100">

          {/* Top row: actions */}
          <div className="flex items-center justify-end gap-2 mb-3">
            {canShowEditDelete && (
              <>
                <button
                  onClick={onEdit}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Modifier l'opérateur ou l'horaire"
                >
                  <Pencil size={18} />
                </button>
                <button
                  onClick={onDelete}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Supprimer"
                >
                  <Trash2 size={18} />
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Fermer"
            >
              <X size={18} />
            </button>
          </div>

          {/* Mission title */}
          <h2 className="text-lg font-semibold text-gray-900 mb-3 leading-tight">
            {task.template.name}
          </h2>

          {/* Badges row: interactive status + recurrence */}
          <div className="flex items-center gap-2 flex-wrap">
            <TaskDetailStatusBadge
              currentStatus={task.status}
              onStatusChange={onStatusChange}
              isAdmin={isAdmin}
            />
            {task.is_recurring && recurrenceRule && getRecurrencePattern(recurrenceRule) && (
              <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded">
                {getRecurrencePattern(recurrenceRule)}
              </span>
            )}
          </div>
        </div>

        {/* ── Body ───────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">

          {/* Zone consultation: information read-only */}
          <div className="px-5 py-4 space-y-3.5">
            <div className="flex items-center text-sm font-medium text-gray-900">
              <Info size={15} className="mr-1.5 text-gray-400 flex-shrink-0" />
              <span>Informations</span>
            </div>
            <InfoRow label="Opérateur assigné">
              <span className="text-sm text-gray-900 font-medium">
                {operator ? getFullName(operator) : 'Non assigné'}
              </span>
            </InfoRow>

            <InfoRow label="Date et heure">
              <span className="text-sm text-gray-900">
                {formatDateTimeRange(
                  task.assigned_date,
                  task.scheduled_time,
                  task.duration_override_minutes || task.template.estimated_duration_minutes
                )}
              </span>
            </InfoRow>

            {serviceName && (
              <InfoRow label="Service">
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getServiceColorClass(serviceColor || '')}`}
                >
                  {serviceName}
                </span>
              </InfoRow>
            )}

            {task.status === 'completed' && task.completed_at && (
              <InfoRow label="Terminée le">
                <span className="text-sm text-emerald-700 font-medium">
                  {formatCompletedAt(task.completed_at)}
                </span>
              </InfoRow>
            )}
          </div>

          {/* Zone action: items + text input fields */}
          <div className="px-5 pb-5">
            <TaskDetailItems
              taskId={task.id}
              items={task.items}
              onTextInputBatchSave={onTextInputBatchSave}
            />
          </div>

          {/* Activity history */}
          <div className="px-5 pb-5">
            <TaskActivityHistory
              taskId={task.isVirtualOccurrence ? (task.parentTaskId ?? task.id) : task.id}
              occurrenceDate={task.isVirtualOccurrence ? task.occurrenceDate : null}
            />
          </div>
        </div>
      </div>
    </div>,
    getPortalRoot()
  );
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      {children}
    </div>
  );
}
